import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useAlertaFaixa } from './alertas/AlertasContexto';

// Faixa de Inconsistencias de Estoque ([DA-341]) — DUAS faixas independentes, uma por fila:
//   - divergencia (`qtd`): total do produto x soma das prateleiras.
//   - negativos: estoque abaixo de zero, no total ou numa prateleira — a fila da [DA-329].
//
// As duas levam a MESMA aba (Divergencia lista os dois achados, com a coluna Motivo separando).
// Ainda assim sao faixas separadas: o texto de cada uma diz o que fazer, e "N produtos com o total
// diferente da soma das prateleiras" seria FALSO sobre um negativo puro. Somar os numeros daria um
// total que nao se resolve em lugar nenhum. As abas `sem endereco` e `sem contagem` seguem FORA
// ([DA-360]): sao centenas de linhas de faxina de fundo e deixariam a faixa acesa pra sempre.
// ⚠️ **`sem endereco` DEIXOU de estar fora em 21/08/2026** — virou a 5a faixa (ver o bloco dela
// abaixo). `sem contagem` continua fora, e pelo mesmo motivo de sempre.
//
// TERCEIRA faixa em 2026-08-07: `Prateleira sem cadastro`, a aba nova. Ela e o caso que a [DA-360]
// mandava deixar de fora — a coluna de origem esta CONGELADA, entao o numero nao cai nem quando
// alguem arruma o produto. O Ruan pediu a faixa sabendo disso; o que sobrou por minha conta foi a
// COR: ouro `#eab308` em vez do laranja das outras duas. As duas de cima apagam quando o problema
// acaba e por isso valem urgencia; esta e faxina de fundo, e vesti-la de laranja gastaria a
// autoridade do laranja nas que de fato pedem acao hoje. Ouro ja existe na escala de gravidade e a
// barra recolhida sabe ranquear.
//
// NAO tem o "X" de dispensar que as outras faixas tem, e isso e deliberado: as dispensaveis avisam
// de algo que o TEMPO resolve (conta que vence, troca que alguem vai validar). Aqui o numero so cai
// quando alguem CORRIGE — dispensar por um dia seria esconder a fila em vez de andar com ela. Mesma
// familia da faixa de divergencia de caixa, que tambem so sai quando o admin resolve.
//
// QUARTA faixa em 2026-08-21: `Produtos sem Estrela` ([DA-601]) — 2+ prateleiras e nenhuma marcada
// como principal. A fila em si nao e nova (e a faxina da [DA-324], ja visivel como lugar virtual em
// `/estoque/localizacoes`); o que mudou foi o CUSTO de deixa-la parada.
//
// Ate 21/08 era cosmetica: o produto mostrava `—` na coluna Local e nada mais. Desde a [DA-601] o
// estorno de pedido devolvido do caixa devolve a mercadoria NA ESTRELA — e produto sem estrela nao
// tem onde voltar, entao cai no fundo, que e' exatamente o defeito que a DA-601 consertou. A faixa
// existe pra que esse buraco nao volte calado.
//
// Diferente da 3a (`texto-livre`), esta APAGA: marcar a estrela na ficha tira o produto da lista.
// Por isso vale ambar `#f59e0b` (o par da faixa de Repasses) e nao o ouro da faxina permanente —
// mas ainda abaixo do laranja das duas primeiras, que falam de estoque errado HOJE.
//
// ⚠️ Nasce APAGADA: em 21/08 sao 0 produtos ativos (os 5 casos do banco sao todos inativos, e
// inativo nao vende, entao nenhum estorno vai procurar a estrela deles). Isso e o alarme
// funcionando, nao quebrado — quem quiser ver os 5 liga "mostrar inativos" na tela.
// QUINTA faixa, no mesmo dia — e' a CORRECAO de um erro meu, nao um recurso novo.
//
// Eu disse a ele que havia "5 produtos sem estrela onde o estorno nao sabe devolver" **sem
// conferir que os 5 eram inativos**. Ele mandou construir a faixa por causa desse numero, e ela
// nasceu contando zero. Ele mandou dois prints e a frase que reabriu o caso: *"nesta situacao o
// sistema nao saberia pra onde devolver, quando uma venda voltar do caixa para orcamentos"*.
//
// ⚠️ Ele descreveu uma SITUACAO; eu tinha entregue um CRITERIO. Produto sem endereco NENHUM cai na
// situacao igual — nao ha estrela pra procurar, a cascata nao acha lugar, e a devolucao nao e'
// registrada em prateleira alguma. Sao **153 ativos**, e **15 estornos ja passaram por 6 deles**.
// A 4a faixa nao cobria nenhum desses.
//
// DOIS numeros no texto, de proposito: o total (153) e o `critico` (18 = com estoque **e** vendendo
// em 90 dias). A [DA-360] tirou esta fila do alarme quando eram 465, porque alarme que nao apaga e'
// papel de parede — e isso continua verdade pros 135 parados. Mostrar so o critico seria eu
// reinterpretando o pedido dele **de novo**; mostrar so o total esconderia por onde comecar.
interface Resumo {
  qtd: number;
  negativos: number;
  texto_livre: number;
  sem_estrela: number;
  sem_endereco: number;
  sem_endereco_critico: number;
}

const POLLING_MS = 5 * 60 * 1000;

export function AlertaInconsistenciasEstoqueBanner() {
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const [resumo, setResumo] = useState<Resumo | null>(null);

  // Estoquista ve: a fila e o trabalho DELE. Vendedora nao — ela nao tem porta pra resolver.
  const podeVer = usuario && ['admin', 'gerente', 'estoquista'].includes(usuario.perfil);
  // A 3a faixa tem publico PROPRIO, mais estreito: "admin e estoquista", palavra do Ruan. Gerente
  // fica de fora aqui de proposito — nao herdar o `podeVer` das outras duas e o que mantem o pedido
  // dele de pe caso alguem mexa naquela lista depois.
  const podeVerTextoLivre = usuario && ['admin', 'estoquista'].includes(usuario.perfil);
  // A 4a idem — "ESTOQUISTA e ADMIN", palavra dele em 21/08. Constante propria pelo mesmo motivo da
  // 3a: publico escrito uma vez por faixa nao escorrega quando alguem mexe na lista do vizinho.
  const podeVerSemEstrela = usuario && ['admin', 'estoquista'].includes(usuario.perfil);

  useEffect(() => {
    if (!podeVer) return;
    let cancelado = false;
    async function tick() {
      try {
        const r = await api.get<Resumo>('/api/localizacoes/inconsistencias-resumo');
        if (!cancelado) setResumo(r);
      } catch { /* silencioso: faixa some, o resto da tela nao pode cair por causa dela */ }
    }
    void tick();
    const id = setInterval(tick, POLLING_MS);
    return () => { cancelado = true; clearInterval(id); };
  }, [podeVer]);

  // Duas filas = duas pastilhas, pelo mesmo motivo de serem duas faixas: somar daria um numero
  // que nao se resolve em lugar nenhum (ver o cabecalho do arquivo).
  const temDivergencia = !!podeVer && !!resumo && resumo.qtd > 0;
  const temNegativo = !!podeVer && !!resumo && resumo.negativos > 0;
  const temTextoLivre = !!podeVerTextoLivre && !!resumo && resumo.texto_livre > 0;
  const temSemEstrela = !!podeVerSemEstrela && !!resumo && resumo.sem_estrela > 0;
  // Mesmo publico das duas: as duas filas se resolvem endereçando o produto, e quem faz isso e o
  // estoquista com o admin olhando.
  const temSemEndereco = !!podeVerSemEstrela && !!resumo && resumo.sem_endereco > 0;

  const recolhido = useAlertaFaixa(
    'estoque-divergencia',
    temDivergencia ? { rotulo: 'Inconsistencias', bg: '#f97316', borda: '#c2410c', qtd: resumo!.qtd } : null,
  );
  useAlertaFaixa(
    'estoque-negativo',
    temNegativo ? { rotulo: 'Estoque negativo', bg: '#f97316', borda: '#c2410c', qtd: resumo!.negativos } : null,
  );
  useAlertaFaixa(
    'estoque-texto-livre',
    temTextoLivre ? { rotulo: 'Prateleira sem cadastro', bg: '#eab308', borda: '#a16207', qtd: resumo!.texto_livre } : null,
  );
  useAlertaFaixa(
    'estoque-sem-estrela',
    temSemEstrela ? { rotulo: 'Sem estrela', bg: '#f59e0b', borda: '#d97706', qtd: resumo!.sem_estrela } : null,
  );
  useAlertaFaixa(
    'estoque-sem-endereco',
    temSemEndereco ? { rotulo: 'Sem endereco', bg: '#f59e0b', borda: '#d97706', qtd: resumo!.sem_endereco } : null,
  );

  if ((!temDivergencia && !temNegativo && !temTextoLivre && !temSemEstrela && !temSemEndereco) || recolhido || !resumo) return null;

  // `aba` vai como state de navegacao: sem ele a tela abre sempre na Divergencia e a faixa levaria o
  // operador pra uma lista vazia falando de outra fila. E MENSAGEM, nao configuracao — a tela obedece
  // uma vez e apaga o state ([DA-420]).
  //
  // `destino`/`state` existem porque a 4a faixa NAO vai pra Inconsistencias: a fila dela ja tem
  // casa desde a [DA-324] — o lugar virtual `sem-principal` em `/estoque/localizacoes`. Criar uma
  // aba nova pro mesmo dado daria duas listas do mesmo problema, que e' o que a [DA-548] cobrou.
  const faixa = (
    chave: string,
    titulo: string,
    texto: string,
    opts?: { bg?: string; borda?: string; aba?: string; destino?: string; state?: unknown },
  ) => (
    <div
      key={chave}
      style={{
        background: opts?.bg ?? '#f97316',
        borderLeft: `4px solid ${opts?.borda ?? '#c2410c'}`,
        color: '#000',
        padding: '8px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontSize: 13,
        cursor: 'pointer',
      }}
      onClick={() => {
        const destino = opts?.destino ?? '/estoque/inconsistencias';
        const state = opts?.state ?? (opts?.aba ? { aba: opts.aba } : undefined);
        navigate(destino, state ? { state } : undefined);
      }}
    >
      <AlertTriangle size={16} />
      <span style={{ fontWeight: 600 }}>{titulo}</span>
      <span>{texto}</span>
      <span style={{ marginLeft: 'auto', textDecoration: 'underline' }}>Conferir</span>
    </div>
  );

  return (
    <>
      {temDivergencia && faixa(
        'divergencia',
        'Inconsistencias de estoque:',
        `${resumo.qtd} ${resumo.qtd === 1 ? 'produto' : 'produtos'} com o total diferente da soma das prateleiras`,
      )}
      {temNegativo && faixa(
        'negativos',
        'Estoque negativo:',
        `${resumo.negativos} ${resumo.negativos === 1 ? 'produto' : 'produtos'} abaixo de zero — costuma ser nota de entrada nao lancada`,
      )}
      {temTextoLivre && faixa(
        'texto-livre',
        'Prateleira sem cadastro:',
        `${resumo.texto_livre} ${resumo.texto_livre === 1 ? 'produto tem' : 'produtos tem'} localizacao gravada na ficha que nao existe no cadastro de Localizacoes`,
        { bg: '#eab308', borda: '#a16207', aba: 'texto-livre' },
      )}
      {temSemEstrela && faixa(
        'sem-estrela',
        'Produtos sem Estrela:',
        `${resumo.sem_estrela} ${resumo.sem_estrela === 1 ? 'produto esta' : 'produtos estao'} em 2 ou mais prateleiras sem nenhuma marcada como principal — o estorno do caixa nao sabe onde devolver`,
        { bg: '#f59e0b', borda: '#d97706', destino: '/estoque/localizacoes', state: { lugarId: 'sem-principal' } },
      )}
      {temSemEndereco && faixa(
        'sem-endereco',
        'Produtos sem endereco:',
        `${resumo.sem_endereco} ${resumo.sem_endereco === 1 ? 'produto ativo nao tem' : 'produtos ativos nao tem'} prateleira cadastrada`
          + `${resumo.sem_endereco_critico > 0
              ? ` — ${resumo.sem_endereco_critico} com estoque e vendendo, e nesses o pedido que volta do caixa nao tem onde ser devolvido`
              : ''}`,
        { bg: '#f59e0b', borda: '#d97706', aba: 'sem-endereco' },
      )}
    </>
  );
}
